import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { adminApi } from "@profitlens/api-client";
import type { AdminPaymentOut, PaymentProvider, PaymentStatus } from "@profitlens/api-client";
import { Badge } from "@profitlens/ui/badge";
import { Button } from "@profitlens/ui/button";
import { Card } from "@profitlens/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@profitlens/ui/dialog";
import { ErrorState } from "@profitlens/ui/error-state";
import { Input } from "@profitlens/ui/input";
import { Label } from "@profitlens/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@profitlens/ui/select";
import { Spinner } from "@profitlens/ui/spinner";
import { Switch } from "@profitlens/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@profitlens/ui/table";

import { PageHeader } from "@/components/page-header";
import { Pagination } from "@/components/pagination";
import { translateError } from "@/lib/api-error";
import { formatDateTime } from "@/lib/format";

const PAGE_SIZE = 20;
const ALL = "__all__";

const STATUSES: PaymentStatus[] = ["pending", "succeeded", "failed", "refunded"];
const PROVIDERS: PaymentProvider[] = ["paypal", "alipay", "wechat", "manual", "mock"];

const STATUS_KEYS: Record<PaymentStatus, string> = {
  pending: "payments.statusPending",
  succeeded: "payments.statusSucceeded",
  failed: "payments.statusFailed",
  refunded: "payments.statusRefunded",
};

const PROVIDER_KEYS: Record<PaymentProvider, string> = {
  paypal: "payments.providerPaypal",
  alipay: "payments.providerAlipay",
  wechat: "payments.providerWechat",
  manual: "payments.providerManual",
  mock: "payments.providerMock",
};

function StatusBadge({ status }: { status: PaymentStatus }) {
  const { t } = useTranslation();
  if (status === "succeeded") return <Badge variant="success">{t(STATUS_KEYS[status])}</Badge>;
  if (status === "refunded") return <Badge variant="warning">{t(STATUS_KEYS[status])}</Badge>;
  if (status === "failed") return <Badge variant="danger">{t(STATUS_KEYS[status])}</Badge>;
  return <Badge variant="secondary">{t(STATUS_KEYS[status])}</Badge>;
}

export function PaymentsPage() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<PaymentStatus | typeof ALL>(ALL);
  const [provider, setProvider] = useState<PaymentProvider | typeof ALL>(ALL);
  const [page, setPage] = useState(1);
  const [refunding, setRefunding] = useState<AdminPaymentOut | null>(null);
  const [reason, setReason] = useState("");
  const [downgrade, setDowngrade] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const list = useQuery({
    queryKey: ["admin", "payments", { status, provider, page }],
    queryFn: () =>
      adminApi.listPayments({
        status: status === ALL ? undefined : status,
        provider: provider === ALL ? undefined : provider,
        page,
        page_size: PAGE_SIZE,
      }),
  });

  const refund = useMutation({
    mutationFn: () => {
      if (!refunding) throw new Error("no payment");
      return adminApi.refundPayment(refunding.id, {
        reason: reason || t("payments.refundDefaultReason"),
        downgrade,
      });
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["admin", "payments"] }),
        queryClient.invalidateQueries({ queryKey: ["admin", "stats"] }),
        queryClient.invalidateQueries({ queryKey: ["admin", "audit-logs"] }),
      ]);
      setRefunding(null);
      setReason("");
      setDowngrade(true);
      setError(null);
    },
    onError: (err) => setError(translateError(err, t)),
  });

  return (
    <div className="space-y-4">
      <PageHeader
        title={t("payments.title")}
        subtitle={t("payments.subtitle")}
        actions={
          <>
            <Select
              value={status}
              onValueChange={(v) => {
                setStatus(v as PaymentStatus | typeof ALL);
                setPage(1);
              }}
            >
              <SelectTrigger className="w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>{t("payments.allStatus")}</SelectItem>
                {STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {t(STATUS_KEYS[s])}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={provider}
              onValueChange={(v) => {
                setProvider(v as PaymentProvider | typeof ALL);
                setPage(1);
              }}
            >
              <SelectTrigger className="w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>{t("payments.allProviders")}</SelectItem>
                {PROVIDERS.map((p) => (
                  <SelectItem key={p} value={p}>
                    {t(PROVIDER_KEYS[p])}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </>
        }
      />

      {list.isLoading ? (
        <div className="grid place-items-center py-12">
          <Spinner className="h-5 w-5" />
        </div>
      ) : list.error ? (
        <ErrorState error={list.error} onRetry={() => list.refetch()} />
      ) : list.data && list.data.items.length === 0 ? (
        <Card className="p-8 text-center text-sm text-muted">{t("payments.empty")}</Card>
      ) : (
        <Card className="overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("payments.colTenant")}</TableHead>
                <TableHead>{t("payments.colProvider")}</TableHead>
                <TableHead>{t("payments.colPlan")}</TableHead>
                <TableHead>{t("payments.colAmount")}</TableHead>
                <TableHead>{t("payments.colStatus")}</TableHead>
                <TableHead>{t("payments.colTime")}</TableHead>
                <TableHead className="w-24">{t("common.actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.data?.items.map((p) => (
                <TableRow key={p.id}>
                  <TableCell className="font-medium">{p.tenant_name ?? "—"}</TableCell>
                  <TableCell>{t(PROVIDER_KEYS[p.provider])}</TableCell>
                  <TableCell>{p.plan_code ?? "—"}</TableCell>
                  <TableCell className="tabular-nums">
                    {p.amount} {p.currency}
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={p.status} />
                  </TableCell>
                  <TableCell className="text-xs whitespace-nowrap text-muted">
                    {formatDateTime(p.occurred_at ?? p.created_at)}
                  </TableCell>
                  <TableCell>
                    {p.status === "succeeded" ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setRefunding(p);
                          setReason("");
                          setDowngrade(true);
                          setError(null);
                        }}
                      >
                        {t("payments.refund")}
                      </Button>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Pagination
            page={page}
            pageSize={PAGE_SIZE}
            total={list.data?.total ?? 0}
            onPage={setPage}
          />
        </Card>
      )}

      <Dialog open={refunding !== null} onOpenChange={(o) => !o && setRefunding(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t("payments.refundTitle")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted">
              {refunding?.tenant_name ?? "—"} ·{" "}
              {refunding ? `${refunding.amount} ${refunding.currency}` : ""}
            </p>
            <div className="space-y-1.5">
              <Label>{t("payments.refundReason")}</Label>
              <Input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={t("payments.refundReasonPlaceholder")}
              />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={downgrade} onCheckedChange={setDowngrade} />
              {t("payments.refundDowngrade")}
            </label>
            {error ? <p className="text-xs text-danger">{error}</p> : null}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRefunding(null)}>
              {t("common.cancel")}
            </Button>
            <Button
              variant="danger"
              disabled={refund.isPending || !refunding}
              onClick={() => refund.mutate()}
            >
              {refund.isPending ? t("common.processing") : t("payments.refundConfirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
